/**
 * Analytics Service
 * Tracks page views, user activity, and generates insights
 */

import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';

export interface PageView {
  userId: string;
  page: string;
  section?: string;
  timestamp: Date;
  sessionId: string;
  userAgent?: string;
  referrer?: string;
}

export interface UserSession {
  userId: string;
  sessionId: string;
  startTime: Date;
  lastActivity: Date;
  isActive: boolean;
  pages: string[];
}

class AnalyticsService {
  private currentSessionId: string | null = null;
  private sessionStartTime: Date | null = null;
  private lastActivityTime: Date | null = null;
  private activityCheckInterval: number | null = null;
  private currentPage: string = '';

  /**
   * Initialize analytics tracking for current user
   */
  init(userId: string) {
    if (this.currentSessionId) return; // Already initialized

    this.currentSessionId = this.generateSessionId();
    this.sessionStartTime = new Date();
    this.lastActivityTime = new Date();

    // Track session start
    this.trackSessionStart(userId);

    // Set up activity heartbeat (every 30 seconds)
    this.activityCheckInterval = window.setInterval(() => {
      this.updateActivity(userId);
    }, 30000);

    // Track activity on user interactions
    this.setupActivityListeners();

    console.log('📊 Analytics initialized for session:', this.currentSessionId);
  }

  /**
   * Clean up analytics tracking
   */
  destroy() {
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
    
    if (this.currentSessionId && auth.currentUser) {
      this.trackSessionEnd(auth.currentUser.uid);
    }

    this.currentSessionId = null;
    this.sessionStartTime = null;
    this.lastActivityTime = null;
  }

  /**
   * Track page view
   */
  async trackPageView(page: string, section?: string) {
    const user = auth.currentUser;
    if (!user || !this.currentSessionId) return;

    this.currentPage = page;
    this.lastActivityTime = new Date();

    try {
      await addDoc(collection(db, 'analytics_pageViews'), {
        userId: user.uid,
        page,
        section: section || null,
        timestamp: serverTimestamp(),
        sessionId: this.currentSessionId,
        userAgent: navigator.userAgent,
        referrer: document.referrer || null
      });

      console.log('📄 Page view tracked:', page, section);
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track custom event
   */
  async trackEvent(
    eventName: string,
    eventData?: Record<string, any>
  ) {
    const user = auth.currentUser;
    if (!user || !this.currentSessionId) return;

    this.lastActivityTime = new Date();

    try {
      await addDoc(collection(db, 'analytics_events'), {
        userId: user.uid,
        sessionId: this.currentSessionId,
        eventName,
        eventData: eventData || {},
        timestamp: serverTimestamp(),
        page: this.currentPage
      });

      console.log('🎯 Event tracked:', eventName, eventData);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track session start
   */
  private async trackSessionStart(userId: string) {
    if (!this.currentSessionId) return;

    try {
      await addDoc(collection(db, 'analytics_sessions'), {
        userId,
        sessionId: this.currentSessionId,
        startTime: serverTimestamp(),
        lastActivity: serverTimestamp(),
        isActive: true,
        pages: [],
        userAgent: navigator.userAgent
      });
    } catch (error) {
      console.error('Failed to track session start:', error);
    }
  }

  /**
   * Update activity timestamp
   */
  private async updateActivity(userId: string) {
    if (!this.currentSessionId) return;

    // Check if user has been inactive for > 5 minutes
    const now = new Date();
    const inactiveTime = this.lastActivityTime 
      ? now.getTime() - this.lastActivityTime.getTime()
      : 0;

    if (inactiveTime > 300000) { // 5 minutes
      // User is inactive, end session
      await this.trackSessionEnd(userId);
      this.destroy();
      return;
    }

    // Update last activity in Firebase (for real-time online users)
    try {
      const sessionsRef = collection(db, 'analytics_onlineUsers');
      const q = query(sessionsRef, where('userId', '==', userId), where('sessionId', '==', this.currentSessionId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // Create online user record
        await addDoc(collection(db, 'analytics_onlineUsers'), {
          userId,
          sessionId: this.currentSessionId,
          lastActivity: serverTimestamp(),
          currentPage: this.currentPage,
          isActive: true
        });
      } else {
        // Update existing record (we'll use a separate update function)
        // For now, just create a new one if needed
      }
    } catch (error) {
      console.error('Failed to update activity:', error);
    }
  }

  /**
   * Track session end
   */
  private async trackSessionEnd(userId: string) {
    if (!this.currentSessionId) return;

    try {
      await addDoc(collection(db, 'analytics_sessionEnds'), {
        userId,
        sessionId: this.currentSessionId,
        endTime: serverTimestamp(),
        duration: this.sessionStartTime 
          ? Date.now() - this.sessionStartTime.getTime()
          : 0
      });

      // Mark user as offline
      const onlineRef = collection(db, 'analytics_onlineUsers');
      const q = query(onlineRef, where('userId', '==', userId), where('sessionId', '==', this.currentSessionId));
      const snapshot = await getDocs(q);
      
      // We'll handle deletion via Firebase rules TTL or admin cleanup
    } catch (error) {
      console.error('Failed to track session end:', error);
    }
  }

  /**
   * Set up activity listeners
   */
  private setupActivityListeners() {
    const updateActivity = () => {
      this.lastActivityTime = new Date();
    };

    // Track user interactions
    document.addEventListener('click', updateActivity);
    document.addEventListener('keypress', updateActivity);
    document.addEventListener('scroll', updateActivity);
    document.addEventListener('mousemove', updateActivity);
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get session duration
   */
  getSessionDuration(): number {
    if (!this.sessionStartTime) return 0;
    return Date.now() - this.sessionStartTime.getTime();
  }

  /**
   * Check if user is active
   */
  isUserActive(): boolean {
    if (!this.lastActivityTime) return false;
    const inactiveTime = Date.now() - this.lastActivityTime.getTime();
    return inactiveTime < 300000; // Active if < 5 minutes since last activity
  }
}

export const analyticsService = new AnalyticsService();

// Auto cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    analyticsService.destroy();
  });
}
